import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getWeekDateRange } from '../utils/timeUtils';
import { getWeekNumber, getWeekStartDate, formatWeekLabel } from '../utils/helpers.ts';
import WeekNavigator from './WeekNavigator';
import HelpIcon from './Documentation/HelpIcon';

const RANGE_WHOLE_PERIOD = 'whole-period';
const RANGE_SINGLE_WEEK = 'single-week';

const ClassSchedulerTimeFrameSelector = ({
    timePeriods,
    selectedPeriodId,
    onMonthChange,
    onWeekChange,
    selectedWeek,
    selectedMonth,
    currentView,
    collapsedPeriodSelect = null
}) => {
    const [selectedRange, setSelectedRange] = useState(RANGE_WHOLE_PERIOD);

    const selectedPeriod = useMemo(
        () => timePeriods.find(p => String(p.id) === String(selectedPeriodId)) ?? null,
        [timePeriods, selectedPeriodId]
    );

    const calendarYearForPeriod = selectedPeriod?.year ?? new Date().getFullYear();

    const periodWeekOptions = useMemo(() => {
        if (
            !selectedPeriod ||
            typeof selectedPeriod.start_week !== 'number' ||
            typeof selectedPeriod.end_week !== 'number'
        ) {
            return [];
        }

        const periodStart = selectedPeriod.start_week;
        const periodEnd = selectedPeriod.end_week;
        const periodYear = calendarYearForPeriod;
        const weeks = [];

        const pushWeek = (week, year) => {
            const weekDateRange = getWeekDateRange(year, week);
            const dateLabel = weekDateRange
                ? `${weekDateRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDateRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : '';

            weeks.push({
                week,
                year,
                label: `Week ${week} (${dateLabel})`
            });
        };

        if (periodStart <= periodEnd) {
            for (let week = periodStart; week <= periodEnd; week++) {
                pushWeek(week, periodYear);
            }
        } else {
            for (let week = periodStart; week <= 53; week++) {
                pushWeek(week, periodYear);
            }
            for (let week = 1; week <= periodEnd; week++) {
                pushWeek(week, periodYear + 1);
            }
        }

        return weeks;
    }, [selectedPeriod, calendarYearForPeriod]);

    const weekInPeriod = useCallback((week, year) => {
        if (!selectedPeriod) return false;
        const periodStart = selectedPeriod.start_week;
        const periodEnd = selectedPeriod.end_week;
        const periodYear = calendarYearForPeriod;

        if (periodStart <= periodEnd) {
            return year === periodYear && week >= periodStart && week <= periodEnd;
        }
        return (year === periodYear && week >= periodStart) || (year === periodYear + 1 && week <= periodEnd);
    }, [selectedPeriod, calendarYearForPeriod]);

    const getDefaultWeekForPeriod = useCallback(() => {
        if (!selectedPeriod) return null;

        const now = new Date();
        let week = getWeekNumber(now);
        let year = now.getFullYear();

        if (!weekInPeriod(week, year)) {
            const first = periodWeekOptions[0];
            return first ? { week: first.week, year: first.year } : null;
        }

        const periodStart = selectedPeriod.start_week;
        const periodEnd = selectedPeriod.end_week;
        if (periodStart > periodEnd) {
            year = week <= periodEnd ? calendarYearForPeriod + 1 : calendarYearForPeriod;
        } else {
            year = calendarYearForPeriod;
        }

        return { week, year };
    }, [selectedPeriod, weekInPeriod, periodWeekOptions, calendarYearForPeriod]);

    const handleRangeChange = (range) => {
        if (currentView === 'room') return;

        setSelectedRange(range);

        if (range === RANGE_WHOLE_PERIOD) {
            if (onMonthChange) onMonthChange(null);
            if (onWeekChange) onWeekChange(null);
            return;
        }

        if (onMonthChange) onMonthChange(null);
        if (!selectedWeek && onWeekChange) {
            const defaultWeek = getDefaultWeekForPeriod();
            if (defaultWeek) onWeekChange(defaultWeek);
        }
    };

    const selectedWeekIndex = useMemo(() => {
        if (!selectedWeek) return -1;
        return periodWeekOptions.findIndex(
            (opt) => opt.week === selectedWeek.week && opt.year === selectedWeek.year
        );
    }, [selectedWeek, periodWeekOptions]);

    const navigateWeek = useCallback((direction) => {
        if (!onWeekChange || periodWeekOptions.length === 0) return;

        let nextIndex = selectedWeekIndex;
        if (nextIndex === -1) {
            nextIndex = direction > 0 ? 0 : periodWeekOptions.length - 1;
        } else {
            nextIndex = (nextIndex + direction + periodWeekOptions.length) % periodWeekOptions.length;
        }

        const next = periodWeekOptions[nextIndex];
        onWeekChange({ week: next.week, year: next.year });
    }, [onWeekChange, periodWeekOptions, selectedWeekIndex]);

    const currentWeekDate = useMemo(() => {
        if (!selectedWeek) return null;
        const range = getWeekDateRange(selectedWeek.year, selectedWeek.week);
        return range?.startDate ?? null;
    }, [selectedWeek]);

    const currentWeekLabel = useMemo(() => {
        if (!currentWeekDate) return 'Select week';
        return formatWeekLabel(getWeekStartDate(currentWeekDate));
    }, [currentWeekDate]);

    const handleWeekSelectFromModal = useCallback((selectedDate) => {
        if (!onWeekChange || !selectedDate) return;

        const week = getWeekNumber(selectedDate);
        const year = selectedDate.getFullYear();

        if (!weekInPeriod(week, year)) return;

        let resolvedYear = year;
        if (selectedPeriod && selectedPeriod.start_week > selectedPeriod.end_week) {
            resolvedYear = week <= selectedPeriod.end_week ? calendarYearForPeriod + 1 : calendarYearForPeriod;
        } else if (selectedPeriod) {
            resolvedYear = calendarYearForPeriod;
        }

        onWeekChange({ week, year: resolvedYear });
    }, [onWeekChange, weekInPeriod, selectedPeriod, calendarYearForPeriod]);

    useEffect(() => {
        if (selectedWeek) {
            setSelectedRange(RANGE_SINGLE_WEEK);
        } else {
            setSelectedRange(RANGE_WHOLE_PERIOD);
        }
    }, [selectedWeek]);

    useEffect(() => {
        if (selectedRange !== RANGE_SINGLE_WEEK) return;
        if (typeof onWeekChange !== 'function') return;
        if (selectedWeek) return;
        if (!selectedPeriod || periodWeekOptions.length === 0) return;

        const defaultWeek = getDefaultWeekForPeriod();
        if (defaultWeek) onWeekChange(defaultWeek);
    }, [selectedRange, selectedWeek, onWeekChange, selectedPeriod, periodWeekOptions, getDefaultWeekForPeriod]);

    useEffect(() => {
        if (currentView === 'room') {
            setSelectedRange(RANGE_WHOLE_PERIOD);
            if (onMonthChange) onMonthChange(null);
            if (onWeekChange) onWeekChange(null);
        }
    }, [currentView, onMonthChange, onWeekChange]);

    useEffect(() => {
        if (selectedMonth && onMonthChange) {
            onMonthChange(null);
        }
    }, [selectedMonth, onMonthChange]);

    const isRoomView = currentView === 'room';
    const showWeekNavigator = selectedRange === RANGE_SINGLE_WEEK && !isRoomView;

    const rangeButtonClass = (isActive) => (
        `px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${isActive
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-800'
        } ${isRoomView ? 'cursor-not-allowed opacity-60' : ''}`
    );

    return (
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 min-h-[2rem]">
            <div className="flex items-center gap-2 justify-self-start">
                <div className="flex items-center gap-1 bg-white/80 rounded-lg p-0.5 shadow-sm ring-1 ring-gray-200/80">
                    <button
                        type="button"
                        onClick={() => handleRangeChange(RANGE_WHOLE_PERIOD)}
                        disabled={isRoomView}
                        className={rangeButtonClass(selectedRange === RANGE_WHOLE_PERIOD)}
                    >
                        Whole Period
                    </button>
                    <button
                        type="button"
                        onClick={() => handleRangeChange(RANGE_SINGLE_WEEK)}
                        disabled={isRoomView || periodWeekOptions.length === 0}
                        className={rangeButtonClass(selectedRange === RANGE_SINGLE_WEEK)}
                        title={periodWeekOptions.length === 0 ? 'Select a period on the timeline first' : undefined}
                    >
                        Single Week
                    </button>
                </div>
                <HelpIcon documentationId="class-scheduler-time-frame" position="relative" size={14} />
            </div>

            <div className="flex min-w-0 items-center justify-center gap-2 justify-self-center [&>div]:mb-0">
                {collapsedPeriodSelect}
                {showWeekNavigator && (
                    <WeekNavigator
                        compact
                        currentWeekLabel={currentWeekLabel}
                        onPrevWeek={() => navigateWeek(-1)}
                        onNextWeek={() => navigateWeek(1)}
                        currentDate={currentWeekDate}
                        onWeekSelect={handleWeekSelectFromModal}
                    />
                )}
            </div>

            <div aria-hidden="true" />
        </div>
    );
};

export default ClassSchedulerTimeFrameSelector;
